import { Component, Input } from "@angular/core";

import { EmploymentContract, ContractLogic, Avatar } from "@salaxy/core";

import { SalaxyContext } from "../../services/index";

/** Provides a standard user interface to edit a worker object. */
@Component({
    selector: "salaxy-contract-list",
    template: `<mat-list>
    <h3 md-subheader style="max-width: 350px">
        <button md-mini-fab style="float: right" (click)="createNew()"><mat-icon>add</mat-icon></button>
        Työsopimukset
    </h3>
    <mat-list-item *ngFor="let contract of list" (click)="setCurrentId(contract.id)" class="click">
        <salaxy-avatar [avatar]="getOtherParty(contract)" style="font-size: 48px;" md-list-avatar></salaxy-avatar>
        <h4 md-line>{{ getOtherParty(contract).displayName }}</h4>
        <p md-line>{{ contract.id }}</p>
    </mat-list-item>
</mat-list>
`,
})
export class NgContractListComponent {

    /**
     * Property based on which the groupedList is grouped.
     */
    @Input()
    public groupBy: "employer" | "worker" | "otherParty" | "year" | "month" | "status" = "status";

    /** Limit number of items to show in the list */
    @Input()
    public limitTo: number;

    /**
     * A comma separated list of statuses that are used as filter for the list.
     * In JavaScript
     */
    public filterStatuses: string | string[];

    constructor(protected context: SalaxyContext) {
    }

    /**
     * Gets the Avatar of other party in a contract:
     * Employer or Worker
     */
    public getOtherParty(contract: EmploymentContract) {
        return ContractLogic.getOtherParty(contract, this.context.session.avatar);
    }

    /** The item that is currently in edit / detail view */
    public get current(): EmploymentContract {
        return this.context.contracts.current;
    }

    /**
     * Delete the item from repository.
     * Note that this may not always be possible. At least check model.isReadOnly
     * @param id - Identifier for the item to be deleted.
     */
    public delete(id: string) {
        this.context.contracts.delete(id);
    }

    /**
     * List grouped based on groupBy value
     */
    public get groupedList() {
        const sortDescending = this.groupBy === "year" || this.groupBy === "month" ? true : false;
        const groupedObj = this.list.reduce((prev, cur) => {
            // Worker as the default
            let key = "Unknown grouping: " + (this.groupBy || "[null]");
            switch (this.groupBy) {
                case "employer":
                    key = cur.employer.avatar.sortableName || "Työnantajaa ei valittu";
                    break;
                case "worker":
                    key = cur.worker.avatar.sortableName || "Työntekijää ei valittu";
                    break;
                case "otherParty":
                    key = this.getOtherParty(cur).sortableName;
                    break;
                case "year":
                    {
                        // TODO: Move the getting of a date to a generic helper
                        const date: any = cur.updatedAt || cur.createdAt || "0000";
                        if (date.getFullYear) {
                            key = "" + date.getFullYear();
                        } else {
                            key = ("" + date).substr(0, 4);
                        }
                    }
                    break;
            }

            if (!prev[key]) {
                prev[key] = [cur];
            } else {
                prev[key].push(cur);
            }
            return prev;
        }, {});
        return Object.keys(groupedObj)
            .map((key) => ({ key, value: groupedObj[key] }))
            .sort((n1, n2) => {
                if (n1.key > n2.key) {
                    return sortDescending ? -1 : 1;
                }
                if (n1.key < n2.key) {
                    return sortDescending ? 1 : -1;
                }
                return 0;
            });
    }

    /**
     * List of contracts
     * The list is filtered if a filter is set
     */
    public get list() {
        const result = this.context.contracts.list;
        if (this.limitTo) {
            return result.slice(0, this.limitTo);
        }
        return result;
    }

    /** Sets the current ID for the contract */
    public setCurrentId(id: string) {
        this.context.contracts.setCurrentId(id);
    }

    /** Creates a new contract */
    public createNew() {
        this.context.contracts.newCurrent();
    }

}
